import type * as DialogPrimitive from "@radix-ui/react-dialog";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  PropsWithChildren,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { TButtonProps, TButtonWidth } from "../../primitives";
import type { DefinedDefaults, TWithDataComponent } from "@/ui/lib";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const DIALOG_CLOSE_ICON: TIconKey = "cross";

export const AVAILABLE_DIALOG_HEADER_SIZES = [ "default", "lg" ] as const;

export const DIALOG_FOOTER_CTAS_VARIANT_CLASS_MAP = {
  inline: "flex-row-reverse justify-start",
  stacked: "flex-col justify-center",
} as const;

export const DIALOG_FOOTER_CTA_VARIANT_BUTTON_WIDTH_MAP: Record<
  TDialogFooterVariant, TButtonWidth
> = {
  inline: "content",
  stacked: "fill",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TDialogFooterVariant = keyof typeof DIALOG_FOOTER_CTAS_VARIANT_CLASS_MAP;
export type TDialogHeaderSize = typeof AVAILABLE_DIALOG_HEADER_SIZES[number];

export type TDialogRootProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root> &
  PropsWithChildren & TWithDataComponent & {
    title: string;
    description?: string;
    header?: TDialogRootHeaderProps;
    footer?: TDialogFooterProps;
  };
export type TDialogFooterCtas = {
  primary?: TButtonProps;
  secondary?: TButtonProps
};
export type TDialogFooterCtaProps = {
  ctas?: TDialogFooterCtas;
  variant?: TDialogFooterVariant;
};
export type TDialogTriggerElement = ComponentRef<typeof DialogPrimitive.Trigger>;
export type TDialogTriggerProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>;
export type TDialogPortalProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>;
export type TDialogOverlayElement = ComponentRef<typeof DialogPrimitive.Overlay>;
export type TDialogOverlayProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
export type TDialogContentElement = ComponentRef<typeof DialogPrimitive.Content>;
export type TDialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;
export type TDialogCloseElement = ComponentRef<typeof DialogPrimitive.Close>;
export type TDialogCloseProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Close>;
export type TDialogTitleElement = ComponentRef<typeof DialogPrimitive.Title>;
export type TDialogTitleProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;
export type TDialogDescriptionElement = ComponentRef<typeof DialogPrimitive.Description>;
export type TDialogDescriptionProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Description>;
export type TDialogHeaderProps = HTMLAttributes<HTMLDivElement> & {
  size?: TDialogHeaderSize;
};
export type TDialogRootHeaderProps = Omit<TDialogHeaderProps, "children">;
export type TDialogFooterProps = HTMLAttributes<HTMLDivElement> & TDialogFooterCtaProps;

type TDialogRootDefaultProps = DefinedDefaults<
  TDialogRootProps,
  "data-component"
>;

type TDialogHeaderDefaultProps = DefinedDefaults<
  TDialogHeaderProps,
  "size"
>;

type TDialogFooterCtaDefaultProps = DefinedDefaults<
  TDialogFooterCtaProps,
  "variant"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const DIALOG_ROOT_DEFAULTS = {
  "data-component": "Dialog",
} satisfies TDialogRootDefaultProps;

export const DIALOG_HEADER_DEFAULTS = {
  size: "default",
} satisfies TDialogHeaderDefaultProps;

export const DIALOG_FOOTER_CTAS_DEFAULTS = {
  variant: "stacked",
} satisfies TDialogFooterCtaDefaultProps;
